import { formatMinorPlain } from "./amount";
import { PayHandle, type PayHandles } from "./events";
import { MAX_MINOR, PROVIDER_CURRENCIES, QR_PROVIDERS, type CurrencyCode, type QrProvider } from "./money";

/**
 * Amount-prefilled public pay links for QR Pay (F-405, F-406, D-019, D-027).
 *
 * These are plain public links to the vendor's own handle: no credentials, no API, no payment
 * verification (the vendor taps "Payment received"). A link is only built when the provider can
 * express the register's currency — generating a link the customer's app rejects at the stall is
 * worse than not offering the provider at all (D-027).
 */

/** Longest note we will put in a pay-link query string. */
export const PAY_NOTE_MAX = 80;

export interface PayLinkInfo {
  provider: QrProvider;
  /** Handle as it appears in the URL (leading $ / @ stripped). */
  handle: string;
  url: string;
  amountMinor: number;
  /** Amount exactly as it appears in the URL, e.g. "12.50" or "1250" for JPY. */
  amountText: string;
  currency: CurrencyCode;
  note: string | null;
}

/** Does this provider support this currency at all (D-027)? */
export function isPayLinkSupported(provider: QrProvider, currency: CurrencyCode): boolean {
  return PROVIDER_CURRENCIES[provider].includes(currency);
}

/** Strip a leading `$` (Cash App) or `@` (Venmo) that a vendor may have typed, and trim. */
export function normalizePayHandle(handle: string): string {
  return String(handle ?? "").trim().replace(/^[$@]+/, "").trim();
}

/** Normalize and validate a handle against `PayHandle`; null when it is not usable in a URL. */
export function validPayHandle(handle: string | undefined | null): string | null {
  if (handle === undefined || handle === null) return null;
  const normalized = normalizePayHandle(handle);
  return PayHandle.safeParse(normalized).success ? normalized : null;
}

/**
 * The payee as the customer's provider app shows it: `$cashtag`, `@venmo-user`, `paypal.me/name`.
 * Null when the handle is not usable. The tender screen puts this in front of everyone at the stall
 * so a swapped handle is caught before anyone pays (D-058).
 */
export function displayPayHandle(provider: QrProvider, handle: string): string | null {
  const clean = validPayHandle(handle);
  if (clean === null) return null;
  switch (provider) {
    case "cashapp":
      return `$${clean}`;
    case "venmo":
      return `@${clean}`;
    case "paypal":
      return `paypal.me/${clean}`;
    default: {
      const exhaustive: never = provider;
      void exhaustive;
      return null;
    }
  }
}

/** The amount string used in pay-link URLs: plain, locale-free, currency's minor digits. */
export function formatPayAmount(amountMinor: number, currency: CurrencyCode): string {
  return formatMinorPlain(amountMinor, currency);
}

function sanitizeNote(note: string | undefined): string | null {
  if (!note) return null;
  // eslint-disable-next-line no-control-regex
  const cleaned = note.replace(/[-]/g, " ").replace(/\s+/g, " ").trim().slice(0, PAY_NOTE_MAX);
  return cleaned.length > 0 ? cleaned : null;
}

function validAmount(amountMinor: number): boolean {
  return Number.isSafeInteger(amountMinor) && amountMinor >= 0 && amountMinor <= MAX_MINOR;
}

/**
 * Build the amount-prefilled pay URL, or null when the provider does not support the currency,
 * the handle is not a valid `PayHandle`, or the amount is not a whole non-negative minor amount.
 */
export function buildPayLink(
  provider: QrProvider,
  handle: string,
  amountMinor: number,
  currency: CurrencyCode,
  note?: string,
): string | null {
  return buildPayLinkInfo(provider, handle, amountMinor, currency, note)?.url ?? null;
}

/** Same as `buildPayLink` but also returns the parts the tender screen displays (F-406). */
export function buildPayLinkInfo(
  provider: QrProvider,
  handle: string,
  amountMinor: number,
  currency: CurrencyCode,
  note?: string,
): PayLinkInfo | null {
  if (!isPayLinkSupported(provider, currency)) return null;
  if (!validAmount(amountMinor)) return null;
  const clean = validPayHandle(handle);
  if (clean === null) return null;

  const amountText = formatPayAmount(amountMinor, currency);
  const cleanNote = sanitizeNote(note);
  let url: string;
  switch (provider) {
    case "cashapp":
      // The cashtag's own account currency applies; the URL carries only the amount.
      url = `https://cash.app/$${encodeURIComponent(clean)}/${amountText}`;
      break;
    case "paypal":
      url = `https://paypal.me/${encodeURIComponent(clean)}/${amountText}${currency}`;
      break;
    case "venmo": {
      const params = `txn=pay&amount=${amountText}${cleanNote ? `&note=${encodeURIComponent(cleanNote)}` : ""}`;
      url = `https://venmo.com/u/${encodeURIComponent(clean)}?${params}`;
      break;
    }
    default: {
      const exhaustive: never = provider;
      void exhaustive;
      return null;
    }
  }
  return { provider, handle: clean, url, amountMinor, amountText, currency, note: cleanNote };
}

/**
 * Providers the tender screen may offer: configured with a valid handle *and* able to express this
 * currency (F-405). Order follows `QR_PROVIDERS` so the buttons never move between sales.
 */
export function availablePayProviders(handles: PayHandles | undefined, currency: CurrencyCode): QrProvider[] {
  if (!handles) return [];
  return QR_PROVIDERS.filter((p) => isPayLinkSupported(p, currency) && validPayHandle(handles[p]) !== null);
}

/** Build every offerable pay link for a charge in one call. */
export function buildPayLinks(
  handles: PayHandles | undefined,
  amountMinor: number,
  currency: CurrencyCode,
  note?: string,
): PayLinkInfo[] {
  const out: PayLinkInfo[] = [];
  for (const provider of availablePayProviders(handles, currency)) {
    const info = buildPayLinkInfo(provider, handles?.[provider] ?? "", amountMinor, currency, note);
    if (info) out.push(info);
  }
  return out;
}
